// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Brim — bootstrap and the screen machine. Owns which screen is up, the room's Net
// (created ONCE per session, never left for a rematch), the motion input source, and
// the rAF draw loop that feeds motion energy → core → render. rAF only DRAWS; the
// round logic advances inside step(), which is timestamp-driven, so a backgrounded tab
// never strands a flinch check. Rules live in game.ts, the stage in render.ts.

import '@ben-gy/game-engine/mobile.css';
import './styles/main.css';

import { hardenViewport } from '@ben-gy/game-engine/mobile';
import { createSfx } from '@ben-gy/game-engine/sound';
import { createStore } from '@ben-gy/game-engine/storage';
import { createNet, roomAppId, setTurnConfig, type Net } from '@ben-gy/game-engine/net';
import { getTurnConfig } from '@ben-gy/game-engine/turn';
import { createRounds, type RoundInfo, type Rounds, type RoundPlayer } from '@ben-gy/game-engine/rematch';
import { clearRoomInUrl, createLobby, createRoomEntry, normalizeRoomCode, setRoomInUrl } from '@ben-gy/game-engine/lobby';
import { resolveName } from '@ben-gy/game-engine/identity';

import { escapeHtml, fmtScore } from './dom';
import { MODE_IDS, modeOf, type Mode } from './modes';
import { createGame, drainEvents, release as releaseGame, step as stepGame, type GameEvent, type GameState } from './game';
import { dailySeed, todayUTC, newSeed, seedToCode } from './seed';
import { Board } from './render';
import { Race, seatOf, createOneShot, type RaceMsg } from './race';
import { runCountdown } from './countdown';
import { botMotion, decideBank, READER } from './bot';
import { makeRng } from '@ben-gy/game-engine/rng';
import {
  createNullSource,
  createSensorSource,
  createSyntheticSource,
  motionExists,
  requestMotionPermission,
  type MotionSource,
  type SensorSource,
  type SourceKind,
  type SyntheticSource,
} from './input';
import { RELEASE_SUPPRESS_MS } from './modes';
import { aboutHtml, ATTRIB, helpHtml, menuHtml, multiResultsHtml, soloResultsHtml } from './ui';

const SLUG = 'brim';

// ── shell ─────────────────────────────────────────────────────────────────────
const app = document.querySelector<HTMLElement>('#app')!;
let content: HTMLElement;

const store = createStore(SLUG);
const sfx = createSfx(store.get<boolean>('muted', false) ?? false);
const reducedMotion = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

let unlocked = false;
function unlock(): void {
  if (unlocked) return;
  unlocked = true;
  try {
    sfx.unlock();
  } catch {
    /* audio unlock is best-effort */
  }
}
window.addEventListener('pointerdown', unlock, { once: true });
window.addEventListener('keydown', unlock, { once: true });

// ── persistent session state ───────────────────────────────────────────────────
let playerName = resolveName(store, () => `Player ${Math.floor(Math.random() * 900 + 100)}`);
void playerName;
let myModeChoice = modeOf(store.get<string>('mode', '')).id;
let hostOptsMode = myModeChoice;

const bestKey = (id: string): string => `best_${id}`;
function bestFor(id: string): number {
  return store.get<number>(bestKey(id), 0) ?? 0;
}
function recordBest(id: string, score: number): boolean {
  const cur = bestFor(id);
  if (score > cur) {
    store.set(bestKey(id), score);
    return true;
  }
  return false;
}

let net: Net | null = null;
let rounds: Rounds | null = null;
let race: Race | null = null;
let roster: RoundPlayer[] = [];
let tally: number[] = [];
let roomCode = '';

// ── motion input ─────────────────────────────────────────────────────────────
let inputMode: SourceKind | null = null;
let inputForced: SourceKind | null = null;
let heldSensor: SensorSource | null = null;
let synthetic: SyntheticSource | null = null; // test hook only
const userPrefersNull = (): boolean => store.get<boolean>('preferNull', false) ?? false;

function isTouchDevice(): boolean {
  return (typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches) || 'ontouchstart' in window;
}

/** Decide the input scheme — MUST be called from a user gesture (iOS permission). */
async function prepareInput(): Promise<void> {
  if (synthetic) {
    inputMode = 'synthetic';
    return;
  }
  if (inputForced) {
    inputMode = inputForced;
    if (inputMode === 'sensor' && !heldSensor) heldSensor = createSensorSource();
    return;
  }
  if (!isTouchDevice() || userPrefersNull() || !motionExists()) {
    inputMode = 'null';
    return;
  }
  const perm = await requestMotionPermission();
  if (perm !== 'granted') {
    inputMode = 'null';
    return;
  }
  const s = heldSensor ?? createSensorSource();
  const ok = await s.ready(900);
  if (ok) {
    heldSensor = s;
    inputMode = 'sensor';
  } else {
    if (!heldSensor) s.dispose();
    inputMode = 'null';
  }
}

function disposeSensor(): void {
  if (heldSensor) {
    heldSensor.dispose();
    heldSensor = null;
  }
  inputMode = null;
}

interface GameSourceHandle {
  source: MotionSource;
  announce: string;
}
function makeGameSource(): GameSourceHandle {
  if (synthetic) return { source: synthetic, announce: '' };
  if (inputMode === 'sensor' && heldSensor) {
    heldSensor.reset();
    return { source: heldSensor, announce: 'Hold the phone dead still' };
  }
  return {
    source: createNullSource(),
    announce: motionExists() ? 'No motion here — hold your nerve, release before it overflows' : 'Release before it overflows',
  };
}

// ── screen lifecycle ────────────────────────────────────────────────────────────
const cleanups: Array<() => void> = [];
function onCleanup(fn: () => void): void {
  cleanups.push(fn);
}
function teardown(): void {
  for (const fn of cleanups.splice(0)) {
    try {
      fn();
    } catch {
      /* a failing teardown must never block the next screen */
    }
  }
}
function setPlaying(on: boolean): void {
  document.body.classList.toggle('playing', on);
}

function mountShell(): void {
  app.innerHTML = `<main class="main-content" id="content"></main><footer class="site-footer">${ATTRIB}</footer>`;
  content = document.getElementById('content')!;
}

function sheet(html: string): void {
  const wrap = document.createElement('div');
  wrap.className = 'overlay';
  wrap.innerHTML = `<div class="sheet">${html}</div>`;
  document.body.appendChild(wrap);
  const close = (): void => wrap.remove();
  wrap.addEventListener('pointerdown', (e) => {
    if (e.target === wrap) close();
  });
  wrap.querySelectorAll('.sheet-close').forEach((b) => b.addEventListener('click', close));
}

function toast(msg: string): void {
  const t = document.createElement('div');
  t.className = 'toast';
  t.textContent = msg;
  document.body.appendChild(t);
  setTimeout(() => t.classList.add('show'), 10);
  setTimeout(() => t.remove(), 1900);
}

// ── menu ────────────────────────────────────────────────────────────────────────
function showMenu(): void {
  teardown();
  disposeSensor();
  setPlaying(false);
  content.innerHTML = menuHtml({ modeId: myModeChoice, best: bestFor(myModeChoice), muted: sfx.muted(), dateStr: todayUTC() });

  content.querySelectorAll<HTMLElement>('.chip').forEach((c) =>
    c.addEventListener('click', () => {
      myModeChoice = modeOf(c.dataset.mode).id;
      store.set('mode', myModeChoice);
      try {
        sfx.play('select');
      } catch {
        /* best-effort */
      }
      showMenu();
    }),
  );
  content.querySelector('#playSolo')!.addEventListener('click', () => void startSolo(false));
  content.querySelector('#playDaily')!.addEventListener('click', () => void startSolo(true));
  content.querySelector('#playFriends')!.addEventListener('click', () => void beginFriends());
  content.querySelector('#help')!.addEventListener('click', () => sheet(helpHtml()));
  content.querySelector('#about')!.addEventListener('click', () => sheet(aboutHtml()));
  content.querySelector('#mute')!.addEventListener('click', () => {
    sfx.setMuted(!sfx.muted());
    store.set('muted', sfx.muted());
    showMenu();
  });

  if (!store.get<boolean>('seenHelp', false)) {
    store.set('seenHelp', true);
    sheet(helpHtml());
  }
}

// ── the game screen (shared by solo + multiplayer) ────────────────────────────────
interface GameConfig {
  mode: Mode;
  state: () => GameState;
  advance: (motionEnergy: number, dtMs: number) => GameEvent[];
  doRelease: () => GameEvent[];
  isOver: () => boolean;
  onOver: () => void;
  onQuit: () => void;
  allowRestart: boolean;
  onRestart?: () => void;
  rivals?: () => Array<{ name: string; score: number; orbIndex: number; finished: boolean }>;
}
interface GameScreen {
  destroy(): void;
}

// The active motion source of the current screen, exposed only so the browser-
// verification hook can drive the REAL input→core path synchronously. Never read by gameplay.
let activeSource: MotionSource | null = null;

function juice(events: GameEvent[]): void {
  for (const e of events) {
    try {
      if (e.t === 'jolt') sfx.play('hit', { pitch: 0.8 + e.k * 0.5 });
      else if (e.t === 'bank') sfx.play('coin', { pitch: 0.8 + Math.min(1, e.amount / 300) * 0.8 });
      else if (e.t === 'bust') sfx.play(e.kind === 'overflow' ? 'explosion' : 'lose');
      else if (e.t === 'orb') sfx.play('select', { pitch: 0.7 });
    } catch {
      /* audio best-effort */
    }
    if (!reducedMotion && 'vibrate' in navigator) {
      try {
        if (e.t === 'jolt') navigator.vibrate(Math.round(8 + e.k * 22));
        else if (e.t === 'bank') navigator.vibrate(12);
        else if (e.t === 'bust') navigator.vibrate([30, 40, 30]);
      } catch {
        /* haptics best-effort */
      }
    }
  }
}

function buildGameScreen(cfg: GameConfig): GameScreen {
  const mode = cfg.mode;
  content.innerHTML = `
    <section class="screen game">
      <div class="hud">
        <span class="hud-mode">${escapeHtml(mode.name)}</span>
        <span class="hud-score" id="hudScore">0</span>
        <span class="hud-extra" id="hudExtra"></span>
        <button class="iconbtn" id="pause" title="Pause">❚❚</button>
        <button class="linkbtn hud-quit" id="quit">Quit</button>
      </div>
      <div class="rivals" id="rivals" hidden></div>
      <div class="board-host" id="boardHost">
        <canvas id="cv"></canvas>
        <div class="announce" id="announce" hidden></div>
      </div>
      <button class="release-bar" id="release"><span>RELEASE</span><small>bank the charge</small></button>
    </section>`;

  const boardHost = content.querySelector<HTMLElement>('#boardHost')!;
  const canvas = content.querySelector<HTMLCanvasElement>('#cv')!;
  const hudScore = content.querySelector<HTMLElement>('#hudScore')!;
  const hudExtra = content.querySelector<HTMLElement>('#hudExtra')!;
  const announce = content.querySelector<HTMLElement>('#announce')!;
  const pauseBtn = content.querySelector<HTMLButtonElement>('#pause')!;
  const releaseBtn = content.querySelector<HTMLButtonElement>('#release')!;
  const rivalsEl = content.querySelector<HTMLElement>('#rivals')!;
  content.querySelector('#quit')!.addEventListener('click', cfg.onQuit);

  const board = new Board(canvas);
  const doResize = (): void => board.resize();
  requestAnimationFrame(doResize);
  window.addEventListener('resize', doResize);
  const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(doResize) : null;
  ro?.observe(boardHost);

  const gs = makeGameSource();
  activeSource = gs.source;
  if (gs.announce) {
    announce.textContent = gs.announce;
    announce.hidden = false;
    setTimeout(() => (announce.hidden = true), 2800);
  }

  let live = false;
  let paused = false;
  let overHandled = false;
  let destroyed = false;
  let last = performance.now();
  let suppressUntil = 0;
  let raf = 0;
  let hudTick = 0;

  const cd = runCountdown(boardHost, sfx, () => {
    if (destroyed) return;
    live = true;
    gs.source.reset();
    last = performance.now();
  });

  function doReleaseAndFx(): void {
    if (destroyed || !live || paused || cfg.isOver()) return;
    const st = cfg.state();
    if (!st.holding) return;
    suppressUntil = performance.now() + RELEASE_SUPPRESS_MS;
    const events = cfg.doRelease();
    board.onEvents(events, reducedMotion);
    juice(events);
    for (const e of events) if (e.t === 'orb') gs.source.reset();
  }
  releaseBtn.addEventListener('click', doReleaseAndFx);
  boardHost.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    doReleaseAndFx();
  });

  function updateHud(state: GameState): void {
    hudScore.textContent = fmtScore(state.score);
    if (mode.oneLife) {
      hudExtra.textContent = state.holding || !state.over ? `×${state.multiplier.toFixed(2).replace(/0+$/, '').replace(/\.$/, '')}` : '';
    } else {
      const n = Math.min(state.orbIndex + 1, mode.orbs);
      hudExtra.textContent = `Orb ${n}/${mode.orbs}`;
    }
    if (cfg.rivals) {
      const rs = cfg.rivals();
      if (rs.length) {
        rivalsEl.hidden = false;
        rivalsEl.innerHTML = rs
          .map((r) => `<span class="rival${r.finished ? ' done' : ''}">${escapeHtml(r.name)} <b>${fmtScore(r.score)}</b></span>`)
          .join('');
      }
    }
  }

  function loop(now: number): void {
    if (destroyed) return;
    const dt = Math.min(80, now - last);
    last = now;
    const state = cfg.state();
    if (live && !paused && !cfg.isOver()) {
      const me = now < suppressUntil ? 0 : gs.source.energy(dt);
      const events = cfg.advance(me, dt);
      for (const e of events) if (e.t === 'orb') gs.source.reset();
      board.onEvents(events, reducedMotion);
      juice(events);
    }
    board.frame(state, { dtMs: dt, reducedMotion });
    if (++hudTick % 2 === 0) updateHud(state);
    if (cfg.isOver() && !overHandled) {
      overHandled = true;
      updateHud(state);
      setTimeout(() => {
        if (!destroyed) cfg.onOver();
      }, 780);
    }
    raf = requestAnimationFrame(loop);
  }
  raf = requestAnimationFrame(loop);
  updateHud(cfg.state());

  function setPaused(p: boolean): void {
    if (destroyed || cfg.isOver()) return;
    paused = p;
    let ov = boardHost.querySelector<HTMLElement>('.pause-ov');
    if (p) {
      if (!ov) {
        ov = document.createElement('div');
        ov.className = 'pause-ov';
        ov.innerHTML = `<div class="pause-card"><h2>Paused</h2>
          <button class="btn primary" id="resume">Resume</button>
          ${cfg.allowRestart ? '<button class="btn" id="restart">Restart</button>' : ''}
          <button class="btn ghost" id="pquit">Quit</button></div>`;
        boardHost.appendChild(ov);
        ov.querySelector('#resume')!.addEventListener('click', () => setPaused(false));
        ov.querySelector('#restart')?.addEventListener('click', () => cfg.onRestart?.());
        ov.querySelector('#pquit')!.addEventListener('click', cfg.onQuit);
      }
    } else {
      ov?.remove();
      last = performance.now();
    }
  }
  pauseBtn.addEventListener('click', () => setPaused(!paused));

  function onKey(e: KeyboardEvent): void {
    if (e.key === ' ' || e.code === 'Space') {
      e.preventDefault();
      doReleaseAndFx();
    } else if (e.key === 'p' || e.key === 'P') setPaused(!paused);
    else if ((e.key === 'r' || e.key === 'R') && cfg.allowRestart) cfg.onRestart?.();
  }
  window.addEventListener('keydown', onKey);

  return {
    destroy() {
      destroyed = true;
      if (activeSource === gs.source) activeSource = null;
      cancelAnimationFrame(raf);
      cd.cancel();
      window.removeEventListener('resize', doResize);
      window.removeEventListener('keydown', onKey);
      ro?.disconnect();
      board.destroy();
    },
  };
}

// ── solo ──────────────────────────────────────────────────────────────────────────
let soloState: GameState | null = null;
let soloDaily = false;
let soloScreen: GameScreen | null = null;

async function startSolo(daily: boolean): Promise<void> {
  await prepareInput();
  teardown();
  setPlaying(true);
  const mode = modeOf(myModeChoice);
  soloDaily = daily;
  const seed = daily ? dailySeed(mode.id, todayUTC()) : newSeed();
  soloState = createGame(mode, seed);

  soloScreen = buildGameScreen({
    mode,
    state: () => soloState!,
    advance: (me, dt) => {
      stepGame(soloState!, me, dt);
      return drainEvents(soloState!);
    },
    doRelease: () => {
      releaseGame(soloState!);
      return drainEvents(soloState!);
    },
    isOver: () => soloState!.over,
    onOver: () => endSolo(),
    onQuit: () => showMenu(),
    allowRestart: true,
    onRestart: () => void startSolo(soloDaily),
  });
  onCleanup(() => soloScreen?.destroy());
}

function endSolo(): void {
  const st = soloState!;
  const isNewBest = recordBest(st.mode.id, st.score);
  showSoloResults(isNewBest);
}

function showSoloResults(isNewBest: boolean): void {
  teardown();
  setPlaying(false);
  const st = soloState!;
  const mode = st.mode;
  content.innerHTML = `
    ${soloResultsHtml({ mode, score: st.score, best: bestFor(mode.id), isNewBest, daily: soloDaily, results: st.results })}
    <div class="menu-actions">
      <button class="btn primary" id="again">Play again</button>
      <button class="btn" id="share">Share ${st.score > 0 ? 'score' : 'these orbs'}</button>
      <button class="btn ghost" id="menu">Main menu</button>
      <button class="btn ghost" id="feedback">Report a problem</button>
    </div>`;
  content.querySelector('#again')!.addEventListener('click', () => void startSolo(soloDaily));
  content.querySelector('#menu')!.addEventListener('click', showMenu);
  content.querySelector('#feedback')!.addEventListener('click', (e) => window.feedback?.open({ returnFocusTo: e.currentTarget as HTMLElement }));
  content.querySelector('#share')!.addEventListener('click', () => void shareSolo(st, mode));
}

function shareSolo(st: GameState, mode: Mode): Promise<void> {
  const head = soloDaily ? `Brim ${todayUTC()} · ${mode.name}` : `Brim · ${mode.name}`;
  const line = st.score > 0 ? `banked ${fmtScore(st.score)} charge` : `couldn't hold these ${mode.name} orbs`;
  const link = soloDaily ? 'https://brim.benrichardson.dev' : `https://brim.benrichardson.dev/?seed=${seedToCode(st.seed)}&m=${mode.id}`;
  return share(`${head} — ${line}\n${link}`);
}

async function share(text: string): Promise<void> {
  try {
    if (navigator.share) {
      await navigator.share({ text });
      return;
    }
  } catch {
    /* cancelled — fall through to copy */
  }
  try {
    await navigator.clipboard.writeText(text);
    toast('Copied to clipboard');
  } catch {
    toast('Could not copy');
  }
}

// ── multiplayer ───────────────────────────────────────────────────────────────────
let netSend: (m: RaceMsg) => void = () => {};
let stOff: () => void = () => {};
let lobby: { repaint(): void; destroy(): void } | null = null;
let lobbyMounted = false;
let gameScreen: GameScreen | null = null;

function repaintLobby(): void {
  if (lobbyMounted) lobby?.repaint();
}

async function beginFriends(): Promise<void> {
  await prepareInput(); // ask for the sensor now, while we still have the tap
  showRoomEntry();
}

function showRoomEntry(): void {
  teardown();
  setPlaying(false);
  content.innerHTML = '<section class="screen"><div id="entry"></div></section>';
  const host = content.querySelector<HTMLElement>('#entry')!;

  const deep = new URL(location.href).searchParams.get('room');
  if (deep) {
    const code = normalizeRoomCode(deep);
    clearRoomInUrl();
    if (code.length >= 3) {
      void enterRoom(code, false);
      return;
    }
  }

  const entry = createRoomEntry({
    container: host,
    onSubmit: (code: string, created: boolean) => void enterRoom(normalizeRoomCode(code), created),
    onCancel: showMenu,
    title: 'Play with friends',
    subtitle: 'Everyone holds the same orbs. Start a room, or type a friend’s code.',
  });
  onCleanup(() => entry.destroy());
}

async function enterRoom(code: string, created: boolean): Promise<void> {
  teardown();
  setPlaying(false);
  roomCode = code;
  setRoomInUrl(code);
  tally = [];
  race = null;

  content.innerHTML = `<section class="screen"><div class="lobby-searching"><span class="spinner"></span> Connecting…</div></section>`;

  try {
    setTurnConfig(await getTurnConfig());
  } catch {
    /* fail open — TURN is an upgrade, never a dependency */
  }

  const n = createNet(
    { appId: roomAppId(SLUG), roomId: code, claimHost: created },
    {
      onHostChange: (_id, isSelfHost) => {
        race?.setHost(isSelfHost);
        repaintLobby();
      },
      onPeers: () => repaintLobby(),
      onPeerLeave: (id) => {
        const seat = seatOf(roster, id);
        if (seat !== null) race?.peerLeft(seat);
        repaintLobby();
        refreshMultiResults();
      },
    },
  );
  net = n;

  const r = createRounds<{ mode: string }>({
    net: n,
    playerName,
    minPlayers: 2,
    roundOpts: () => ({ mode: myModeChoice }),
    onRound: (info) => startNetRound(info),
    onChange: (s) => {
      const ho = s.hostOpts as { mode?: string } | null;
      if (ho && typeof ho.mode === 'string') hostOptsMode = modeOf(ho.mode).id;
      repaintLobby();
    },
  });
  rounds = r;

  const sendStatus = n.channel<RaceMsg>('st', (data, from) => {
    if (!race) return;
    const seat = seatOf(roster, from);
    if (seat === null) return;
    race.receive(data, seat);
    refreshMultiResults();
  });
  netSend = (m) => sendStatus(m);
  stOff = () => sendStatus.off();

  showLobby();
}

function showLobby(): void {
  const n = net;
  const r = rounds;
  if (!n || !r) return;
  lobbyMounted = true;
  setPlaying(false);

  content.innerHTML = `<section class="screen"><div id="lobby-host"></div></section>`;
  const isHost = (): boolean => n.isHost() && n.hostSettled();

  lobby = createLobby({
    container: content.querySelector<HTMLElement>('#lobby-host')!,
    net: n,
    rounds: r,
    roomCode,
    minPlayers: 2,
    maxPlayers: 6,
    onCancel: () => void leaveRoom(),
    modeSlot: () => {
      if (isHost()) {
        return `<div class="lobby-modes" id="lm">${MODE_IDS.map(
          (id) =>
            `<button class="chip mode-btn${id === myModeChoice ? ' on' : ''}" data-mode="${id}" aria-pressed="${id === myModeChoice}">${escapeHtml(modeOf(id).name)}</button>`,
        ).join('')}<p class="mode-blurb">${escapeHtml(modeOf(myModeChoice).blurb)}</p></div>`;
      }
      return `<p class="host-pick">${n.hostSettled() ? `The host chose <strong>${escapeHtml(modeOf(hostOptsMode).name)}</strong>` : 'Waiting for the host…'}</p>`;
    },
    onModeMount: () => {
      if (!isHost()) return;
      content.querySelectorAll<HTMLElement>('#lm .mode-btn').forEach((b) =>
        b.addEventListener('click', () => {
          myModeChoice = modeOf(b.dataset.mode).id;
          store.set('mode', myModeChoice);
          lobby?.repaint();
        }),
      );
    },
  });
  onCleanup(() => {
    lobby?.destroy();
    lobby = null;
  });
}

function startNetRound(info: RoundInfo<{ mode: string }>): void {
  teardown();
  lobbyMounted = false;
  roster = info.players;
  if (tally.length !== roster.length) tally = Array(roster.length).fill(0);
  const mode = modeOf(info.opts?.mode);
  const selfSeat = info.seated ? seatOf(roster, net?.selfId ?? '') : null;
  const names = roster.map((p) => p.name);

  const rc = new Race({
    mode,
    seed: info.seed,
    selfSeat,
    names,
    isHost: info.isHost,
    deps: {
      send: (m) => netSend(m),
      onChange: () => refreshMultiResults(),
      onSelfEnd: () => {
        rounds?.finish();
        setTimeout(() => showMultiResults(), 820);
      },
    },
  });
  race = rc;
  setPlaying(true);

  if (selfSeat === null) {
    showMultiResults();
    return;
  }

  gameScreen = buildGameScreen({
    mode,
    state: () => rc.state,
    advance: (me, dt) => rc.step(me, dt),
    doRelease: () => rc.release(),
    isOver: () => rc.state.over,
    onOver: () => {
      /* onSelfEnd already scheduled the results */
    },
    onQuit: () => backToLobby(),
    allowRestart: false,
    rivals: () =>
      [...rc.results.values()]
        .filter((p) => p.seat !== rc.selfSeat && !p.left)
        .map((p) => ({ name: p.name, score: p.score, orbIndex: p.orbIndex, finished: p.finished })),
  });
  onCleanup(() => {
    gameScreen?.destroy();
    gameScreen = null;
  });
}

// ── multiplayer results ──────────────────────────────────────────────────────────
let resultsMounted = false;
let winsAwarded = false;
const closeWait = createOneShot(25_000, () => refreshMultiResults());

function showMultiResults(): void {
  teardown();
  setPlaying(false);
  resultsMounted = true;
  winsAwarded = false;
  content.innerHTML = `
    <section class="screen results" id="mres"></section>
    <div class="menu-actions" id="mact">
      <button class="btn primary" id="again">Play again</button>
      <p class="waiting-note" id="waiting" aria-live="polite"></p>
      <button class="btn" id="lobby">Back to lobby</button>
      <button class="btn ghost" id="menu">Main menu</button>
      <button class="btn ghost" id="feedback">Report a problem</button>
    </div>`;
  const again = content.querySelector<HTMLButtonElement>('#again')!;
  again.addEventListener('click', () => {
    rounds?.vote();
    again.disabled = true;
    again.textContent = 'Waiting for the others…';
  });
  content.querySelector('#lobby')!.addEventListener('click', () => backToLobby());
  content.querySelector('#menu')!.addEventListener('click', () => void leaveRoom());
  content.querySelector('#feedback')!.addEventListener('click', (e) => window.feedback?.open({ returnFocusTo: e.currentTarget as HTMLElement }));
  onCleanup(() => {
    resultsMounted = false;
    closeWait.cancel();
  });
  closeWait.poke();
  refreshMultiResults();
}

function refreshMultiResults(): void {
  if (!resultsMounted || !race) return;
  const rc = race;
  const box = content.querySelector<HTMLElement>('#mres');
  if (!box) return;

  if (!winsAwarded && rc.everyoneDone()) {
    winsAwarded = true;
    const top = rc.ranked().find((r) => !r.left);
    if (top && top.score > 0) tally[top.seat] = (tally[top.seat] ?? 0) + 1; // most banked
  }

  box.innerHTML = multiResultsHtml(rc.ranked(), rc.selfSeat, rc.mode, tally);

  const waiting = content.querySelector<HTMLElement>('#waiting');
  if (waiting) {
    const r = rounds?.state();
    if (r && r.startsInMs !== null) {
      waiting.textContent = `Next round in ${Math.ceil(r.startsInMs / 1000)}s — ${r.votes.length}/${Math.max(r.present.length, 1)} ready`;
    } else if (!rc.everyoneDone()) {
      const left = [...rc.results.values()].filter((x) => !x.finished && !x.left).map((x) => x.name);
      waiting.textContent = left.length ? `Still holding: ${left.join(', ')}` : '';
    } else if (r && r.votes.length) {
      waiting.textContent = `${r.votes.length}/${Math.max(r.present.length, 1)} ready for another round`;
    } else {
      waiting.textContent = '';
    }
  }
}

function backToLobby(): void {
  teardown();
  race = null;
  if (net && rounds) showLobby();
  else showMenu();
}

async function leaveRoom(): Promise<void> {
  teardown();
  lobbyMounted = false;
  race = null;
  disposeSensor();
  const n = net;
  const r = rounds;
  net = null;
  rounds = null;
  netSend = () => {};
  try {
    stOff();
  } catch {
    /* channel may already be gone */
  }
  stOff = () => {};
  r?.destroy();
  if (n) {
    try {
      await n.leave();
    } catch {
      /* leaving is best-effort */
    }
  }
  showMenu();
}

// ── boot ────────────────────────────────────────────────────────────────────────
function boot(): void {
  hardenViewport();
  mountShell();
  const params = new URL(location.href).searchParams;
  const room = params.get('room');
  const seedParam = params.get('seed');
  if (room && normalizeRoomCode(room).length >= 3) {
    void beginFriends();
  } else if (seedParam) {
    const m = params.get('m');
    if (m) myModeChoice = modeOf(m).id;
    const seedNum = parseInt(seedParam.trim().toLowerCase(), 36);
    if (Number.isFinite(seedNum) && seedNum > 0) {
      clearRoomInUrl();
      void startSeed(seedNum >>> 0);
      return;
    }
    showMenu();
  } else {
    showMenu();
  }
  window.addEventListener('beforeunload', () => {
    void net?.leave();
  });
}

async function startSeed(seed: number): Promise<void> {
  await prepareInput();
  teardown();
  setPlaying(true);
  const mode = modeOf(myModeChoice);
  soloDaily = false;
  soloState = createGame(mode, seed);
  soloScreen = buildGameScreen({
    mode,
    state: () => soloState!,
    advance: (me, dt) => {
      stepGame(soloState!, me, dt);
      return drainEvents(soloState!);
    },
    doRelease: () => {
      releaseGame(soloState!);
      return drainEvents(soloState!);
    },
    isOver: () => soloState!.over,
    onOver: () => endSolo(),
    onQuit: () => showMenu(),
    allowRestart: true,
    onRestart: () => void startSolo(false),
  });
  onCleanup(() => soloScreen?.destroy());
}

// The browser-verification pass drives this to prove motion → spill and the READER
// completing a run, WITHOUT a real sensor (synthetic feed / headless policy).
(window as unknown as { __brim?: unknown }).__brim = {
  useSynthetic(): void {
    synthetic = createSyntheticSource();
  },
  forceInput(kind: SourceKind): void {
    inputForced = kind;
  },
  setMotion(e: number): void {
    synthetic?.set(e);
  },
  state(): unknown {
    const s = soloState ?? race?.state;
    if (!s) return null;
    return {
      orbIndex: s.orbIndex,
      pot: s.pot,
      score: s.score,
      over: s.over,
      holding: s.holding,
      agitation: s.agitation,
      threshold: s.threshold,
      multiplier: s.multiplier,
      results: s.results.map((r) => ({ banked: r.banked, bust: r.bust, bustKind: r.bustKind })),
      lastBust: s.lastBust,
      mode: s.mode.id,
    };
  },
  release(): void {
    const s = soloState ?? race?.state;
    if (s && s.holding && !s.over) {
      if (soloState) releaseGame(soloState);
      else race?.release();
    }
  },
  // Advance the CURRENT game synchronously (rAF is throttled in a hidden tab). With
  // bot:true a READER policy banks on the tell and drives the run to over (proving the
  // objective is completable). Otherwise it steps under the active source's energy
  // (a synthetic feed → spill).
  stepFor(ms: number, bot = false): unknown {
    const s = soloState ?? race?.state;
    if (!s) return null;
    const rng = makeRng(0x1234abcd);
    const chunk = 1000 / 60;
    for (let t = 0; t < ms && !s.over; t += chunk) {
      if (bot) {
        if (decideBank(s, READER)) {
          if (soloState) releaseGame(soloState);
          else race?.release();
          continue;
        }
        const e = botMotion(s, READER, rng);
        if (soloState) stepGame(soloState, e, chunk);
        else race?.step(e, chunk);
      } else {
        const e = activeSource?.energy(chunk) ?? 0;
        if (soloState) stepGame(soloState, e, chunk);
        else race?.step(e, chunk);
      }
    }
    return { orbIndex: s.orbIndex, score: s.score, over: s.over, holding: s.holding, lastBust: s.lastBust };
  },
};

boot();
